<template>
  <div class="course-list" :class="{ 'has-expanded-course': expandedCourseId }">
    <!-- 添加提示信息 -->

    <div v-if="!filteredCourses.length" class="empty-state">
      没有找到相关课程
    </div>
    <div v-else class="courses-grid">
      <div 
        v-for="course in filteredCourses" 
        :key="course.id" 
        class="course-card"
        :class="{ 'blurred': expandedCourseId && expandedCourseId !== course.id }"
        @click="$emit('course-click', course, $event)"
      >
        <div class="course-image" :style="getCourseImageStyle(course)">
          <button 
            class="favorite-btn" 
            @click.stop="$emit('favorite-click', course.id)"
            :title="course.isFavorite ? '取消收藏' : '收藏课程'"
            :class="{ 'is-favorite': course.isFavorite }"
          >
            ❤
          </button>
        </div>
        <div class="course-content">
          <h3 class="course-title">{{ course.name }}</h3>
          <p class="course-description">
            {{ course.teacherName ? `讲师: ${course.teacherName}` : '暂无讲师信息' }}
          </p>
          <div class="course-meta">
            <span class="course-semester">{{ getSemesterLabel(course) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    courses: {
      type: Array,
      default: () => []
    },
    expandedCourseId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      selectedSemester: 'all' // 默认显示全部
    };
  },
  computed: {
    filteredCourses() {
      if (this.selectedSemester === 'all') {
        return this.courses;
      }
      
      return this.courses.filter(course => {
        const semester = this.determineSemester(course);
        return semester === this.selectedSemester;
      });
    }
  },
  methods: {
    setSemester(semester) {
      this.selectedSemester = semester;
    },
    determineSemester(course) {
      // 从 startDate 判断学期
      if (!course.startDate) return 'unknown';
      
      const dateMatch = course.startDate.match(/(\d{4})-(\d{2})-\d{2}/);
      if (!dateMatch) return 'unknown';
      
      const month = parseInt(dateMatch[2], 10);
      
      // 1-6月为春季学期，7-12月为秋季学期
      return month >= 1 && month <= 6 ? 'spring' : 'fall';
    },
    getSemesterLabel(course) {
      const semester = this.determineSemester(course);
      // 添加年份显示
      const year = course.startDate ? course.startDate.substring(0, 4) : '';
      return semester === 'spring' ? `${year}年春季学期` : 
             semester === 'fall' ? `${year}年秋季学期` : '未知学期';
    },
    
    getCourseImageStyle(course) {
      const baseStyle = {
        width: '100%',
        height: '160px'  // 与 CSS 中的 course-image 高度保持一致
      };
      
      if (course.imgUrl) {
        return {
          ...baseStyle,
          backgroundImage: `url(${course.imgUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        };
      }
      
      return {
        ...baseStyle,
        background: 'linear-gradient(45deg, #f8b62d 25%, #ffcd5d 25%, #ffcd5d 50%, #f8b62d 50%, #f8b62d 75%, #ffcd5d 75%, #ffcd5d 100%)',
        backgroundSize: '40px 40px'
      };
    }
  }
};
</script>

<style scoped>
/* 添加学期筛选样式 */
.semester-filter {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  /* box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); */
  /* border: 1px solid rgba(0, 0, 0, 0.05); */
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.filter-label {
  /* font-weight: bold; */
  color: #333;
  font-size: 14px;
  white-space: nowrap;
}

.filter-options {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 6px 16px;
  border: 1px solid #eee;
  border-radius: 6px;
  background-color: white;
  color: #666;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  font-size: 14px;
}

.filter-btn:hover {
  border-color: #f8b62d;
  color: #f8b62d;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(248, 182, 45, 0.15);
}

.filter-btn.active {
  background-color: #f8b62d;
  color: white;
  border-color: #f8b62d;
  box-shadow: 0 2px 8px rgba(248, 182, 45, 0.2);
}

.course-semester {
  background-color: #f0f7ff;
  color: #3a8ee6;
  padding: 3px 8px;
  border-radius: 4px;
}

/* 现有样式保持不变 */
.course-card.blurred {
  /* 移除模糊效果 */
  filter: none;
  pointer-events: none;
  opacity: 0.7;
  transition: all 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.course-list {
  width: 100%;
  transition: filter 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.course-list.has-expanded-course {
  filter: none;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.course-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 8px 8px 20px 0 rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.course-card:hover {
  /* transform: scale(1.02); 改为整体放大而不是上移 */
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* 移除 course-card:hover .course-image 的单独缩放效果 */
.course-card:hover .course-image {
  /* transform: scale(1.05); */
}

.course-content {
  padding: 15px;
  /* 添加以下样式确保内容区域占据剩余空间 */
  flex: 1;
  display: flex;
  flex-direction: column;
}

.favorite-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #999;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.favorite-btn:hover {
  transform: scale(1.1);
  color: #f8b62d;
}

/* 添加已收藏状态的样式 */
.favorite-btn.is-favorite {
  color: #f8b62d;
}

.favorite-btn.is-favorite:hover {
  color: #999;
}

.course-image {
  position: relative; /* 添加这行以支持绝对定位的收藏按钮 */
  height: 160px;
  width: 100%;
  background-color: #f0f0f0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  transition: transform 0.3s ease;
  /* 添加以下样式确保图片占满 */
  object-fit: cover;
  display: block;
}

.course-image-large {
  width: 100%;
  height: 300px;
  background-color: #f0f0f0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 8px;
  margin-bottom: 20px;
}

.course-content {
  padding: 15px;
}

.course-title {
  margin: 0 0 10px;
  font-size: 18px;
  color: #333;
}

.course-description {
  margin: 0 0 15px;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

.course-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: #888;
}

.course-author, .course-time {
  background-color: #f5f5f5;
  padding: 3px 8px;
  border-radius: 4px;
}


</style>